import { env } from '../config/environment';
import { safeGetItem, safeSetItem } from './safeStorage';

/**
 * Utilitário para normalizar timestamps em todo o sistema
 * Garante que todos os timestamps usem formato ISO 8601
 */

/**
 * Normaliza um timestamp para formato ISO 8601
 */
export function normalizeTimestamp(timestamp: any): string {
  if (!timestamp) {
    return new Date().toISOString();
  }

  if (typeof timestamp === 'string') {
    try {
      // Verificar se já está em formato ISO
      if (timestamp.includes('T') && timestamp.includes('Z')) {
        // Validar se é um ISO válido
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return timestamp;
        }
      }
      
      // Tentar converter para ISO
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    } catch (error) {
      if (env.features.enableDebugMode) {
        console.warn('Erro ao normalizar timestamp:', timestamp, error);
      }
    }
  }

  if (timestamp instanceof Date) {
    if (!isNaN(timestamp.getTime())) {
      return timestamp.toISOString();
    }
  }

  if (typeof timestamp === 'number') {
    try {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return date.toISOString();
      }
    } catch (error) {
      if (env.features.enableDebugMode) {
        console.warn('Erro ao converter timestamp numérico:', timestamp, error);
      }
    }
  }

  // Se não conseguir normalizar, retornar timestamp atual
  if (env.features.enableDebugMode) {
    console.warn('Timestamp inválido, usando timestamp atual:', timestamp);
  }
  return new Date().toISOString();
}

/**
 * Normaliza todos os campos de timestamp em um objeto
 */
export function normalizeObjectTimestamps(obj: any): any {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => normalizeObjectTimestamps(item));
  }

  const normalized = { ...obj };
  const timestampFields = [
    'createdAt', 'created_at',
    'updatedAt', 'updated_at',
    'date',
    'timestamp',
    'deletedAt', 'deleted_at'
  ];

  for (const field of timestampFields) {
    if (normalized[field]) {
      normalized[field] = normalizeTimestamp(normalized[field]);
    }
  }

  return normalized;
}

/**
 * Normaliza timestamps em uma lista de objetos
 */
export function normalizeArrayTimestamps<T>(array: T[]): T[] {
  if (!Array.isArray(array)) {
    return array;
  }

  return array.map(item => normalizeObjectTimestamps(item));
}

/**
 * Valida se um timestamp está em formato ISO válido
 */
export function isValidISOTimestamp(timestamp: string): boolean {
  if (typeof timestamp !== 'string') {
    return false;
  }

  try {
    const date = new Date(timestamp);
    return !isNaN(date.getTime()) && timestamp.includes('T') && timestamp.includes('Z');
  } catch {
    return false;
  }
}

/**
 * Migra timestamps de um objeto do formato antigo para o novo
 */
export function migrateTimestamps(obj: any): any {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const migrated = { ...obj };

  // Migrar created_at para createdAt se necessário
  if (migrated.created_at && !migrated.createdAt) {
    migrated.createdAt = normalizeTimestamp(migrated.created_at);
    delete migrated.created_at;
  } else if (migrated.createdAt) {
    migrated.createdAt = normalizeTimestamp(migrated.createdAt);
  }

  // Normalizar updated_at se existir
  if (migrated.updated_at) {
    migrated.updated_at = normalizeTimestamp(migrated.updated_at);
  }
  // Remover updatedAt se existir (migração para updated_at)
  if (migrated.updatedAt) {
    migrated.updated_at = normalizeTimestamp(migrated.updatedAt);
    delete migrated.updatedAt;
  }

  // Migrar deleted_at para deletedAt se necessário
  if (migrated.deleted_at && !migrated.deletedAt) {
    migrated.deletedAt = normalizeTimestamp(migrated.deleted_at);
    delete migrated.deleted_at;
  } else if (migrated.deletedAt) {
    migrated.deletedAt = normalizeTimestamp(migrated.deletedAt);
  }

  // Normalizar outros campos de data
  if (migrated.date) {
    migrated.date = normalizeTimestamp(migrated.date);
  }

  // O campo 'time' em agendamentos deve permanecer como string HH:MM
  // Não normalizar como timestamp

  return migrated;
}

/**
 * Migra dados do localStorage para usar timestamps normalizados
 */
export function migrateLocalStorageTimestamps(): void {
  try {
    const keys = ['psicosys_patients', 'psicosys_appointments', 'psicosys_sessions'];
    
    for (const key of keys) {
      const data = safeGetItem(key);
      if (data) {
        try {
          const parsed = JSON.parse(data);
          let migrated;
          
          if (Array.isArray(parsed)) {
            migrated = parsed.map(item => migrateTimestamps(item));
          } else if (typeof parsed === 'object') {
            // Para sessões que podem estar em formato Record<string, Session[]>
            migrated = {} as Record<string, any>;
            for (const [patientId, sessions] of Object.entries(parsed)) {
              if (Array.isArray(sessions)) {
                migrated[patientId] = sessions.map(session => migrateTimestamps(session));
              } else {
                migrated[patientId] = migrateTimestamps(sessions);
              }
            }
          } else {
            migrated = migrateTimestamps(parsed);
          }
          
          safeSetItem(key, JSON.stringify(migrated));
          
          if (env.features.enableDebugMode) {
            console.log(`Timestamps migrados para ${key}`);
          }
        } catch (error) {
          if (env.features.enableDebugMode) {
            console.error(`Erro ao migrar timestamps para ${key}:`, error);
          }
        }
      }
    }
    
    // Migrar timestamp de última sincronização
    const lastSyncTime = safeGetItem('psicosys_last_sync_time');
    if (lastSyncTime && !isValidISOTimestamp(lastSyncTime)) {
      const normalizedSyncTime = normalizeTimestamp(lastSyncTime);
      safeSetItem('psicosys_last_sync_time', normalizedSyncTime);
      
      if (env.features.enableDebugMode) {
        console.log('Timestamp de última sincronização migrado');
      }
    }
    
  } catch (error) {
    if (env.features.enableDebugMode) {
      console.error('Erro durante migração de timestamps:', error);
    }
  }
}

/**
 * Utilitário para comparar timestamps normalizados
 */
export function compareTimestamps(timestamp1: any, timestamp2: any): number {
  const ts1 = normalizeTimestamp(timestamp1);
  const ts2 = normalizeTimestamp(timestamp2);
  
  const date1 = new Date(ts1).getTime();
  const date2 = new Date(ts2).getTime();
  
  return date1 - date2;
}

/**
 * Verifica se um timestamp é mais recente que outro
 */
export function isTimestampNewer(timestamp1: any, timestamp2: any): boolean {
  return compareTimestamps(timestamp1, timestamp2) > 0;
}

/**
 * Normaliza especificamente objetos de agendamento preservando o formato HH:MM do campo time
 */
export function normalizeAppointmentTimestamps(appointment: any): any {
  if (!appointment || typeof appointment !== 'object') {
    return appointment;
  }

  const normalized = { ...appointment };
  
  // Normalizar apenas campos de timestamp relevantes para Appointment, preservando 'time' e 'date'
  // Importante: 'date' deve permanecer como string YYYY-MM-DD e NÃO deve ser convertido para ISO
  const timestampFields = [
    'createdAt', 'created_at',
    'updatedAt', 'updated_at',
    // 'date',  // NÃO normalizar 'date' para evitar offset de -1 dia
    'timestamp',
    'deletedAt', 'deleted_at'
  ];

  for (const field of timestampFields) {
    if (normalized[field]) {
      normalized[field] = normalizeTimestamp(normalized[field]);
    }
  }

  // Backward-compat: se 'date' vier como ISO (contém 'T'), converter para YYYY-MM-DD
  if (typeof normalized.date === 'string' && normalized.date.includes('T')) {
    try {
      // extrair somente a parte da data
      normalized.date = normalized.date.split('T')[0];
    } catch {
      // manter como está em caso de erro
    }
  }

  // O campo 'time' deve permanecer como string no formato HH:MM
  // Não aplicar normalização de timestamp
  
  return normalized;
}